import { types, Instance, flow, getEnv, applySnapshot } from "mobx-state-tree";
import log from "loglevelnext";
import { ServerIntegration } from "./server/ServerIntegration";

const EMAIL_OCTOPUS_INTEGRATION_NAME = 'emailoctopus';
const POS_INTEGRATIONS = ['restolution'];

export const Integration = types.model("Integration")
.props({
  id: types.maybeNull(types.string),
  created_at: types.maybeNull(types.string),
  edited_at: types.maybeNull(types.string),
  credentials_type: types.maybeNull(types.string),
  credentials: types.maybeNull(types.frozen()),
  status: types.maybeNull(types.string),
  name: types.maybeNull(types.string),
  advanced: types.maybeNull(types.boolean),
  integrationId: types.maybeNull(types.string)
});

export type IIntegration = Instance<typeof Integration>;

export const IntegrationStore = types.model("IntegrationStore")
  .props({
    integrations: types.optional(types.array(Integration), []),
    availableIntegrations: types.optional(types.late(() => types.array(Integration)), []),
  })
  .views(self => ({
    get emailOctopus() {
      const integration = self.integrations.find(integration => integration.name === EMAIL_OCTOPUS_INTEGRATION_NAME);
      if (!integration) return null;
      
      return {
        id: integration.id,
        integrationId: integration.integrationId,
        apiKey: integration.credentials?.ct?.apikey,
        status: integration.status,
        isProPlan: integration.advanced ? true : false
      };
    },
    
    get hasPosIntegrations() {
      return self.integrations.some(integration => 
        integration.name && POS_INTEGRATIONS.includes(integration.name)
      );
    }
  }))
  .actions(self => ({
    fetchIntegrations: flow(function* (requestSource: string = "init") {
      log.debug("IntegrationStore: fetchIntegrations");
      try {
        const availableIntegrationsResponse = yield getEnv(self).api.fetchAvailableIntegrations(undefined, requestSource);
        const integrationsResponse = yield getEnv(self).api.fetchIntegrations(undefined, requestSource);

        if (availableIntegrationsResponse?.modified) {
          self.availableIntegrations = availableIntegrationsResponse.payload.data.integrations.filter((integration: ServerIntegration) => integration.status === "ENABLED");
        }

        if (integrationsResponse?.modified && integrationsResponse.payload.data.integrations?.length > 0) {
          self.integrations = integrationsResponse.payload.data.integrations;
        }
      } catch (error) {
        log.error("Failed to fetch integrations", error);
      }
    }),

    addEmailOctopusIntegration(id: string, integrationId: string, status: string, advanced: boolean | undefined) {
      const existingIndex = self.integrations.findIndex(integration => integration.name === EMAIL_OCTOPUS_INTEGRATION_NAME);
      
      if (existingIndex >= 0) {
        self.integrations[existingIndex] = {
          ...self.integrations[existingIndex],
          id,
          integrationId,
          status,
          advanced: advanced || false
        };
      } else {
        self.integrations.push({
          id,
          integrationId,
          status,
          name: EMAIL_OCTOPUS_INTEGRATION_NAME,
          advanced: advanced || false
        });
      }
    },

    postIntegration: flow(function* (apiKey: string, advanced: boolean, requestSource: string = "init") {
      log.debug("IntegrationStore: postIntegration:", apiKey);
      const emailOctopusIntegration = self.availableIntegrations.find(integration => integration.name === EMAIL_OCTOPUS_INTEGRATION_NAME);

      if (emailOctopusIntegration && emailOctopusIntegration.id) {
        try {
          const response = yield getEnv(self).api.postIntegration(apiKey, emailOctopusIntegration.id, advanced, undefined, requestSource);
          // Optionally update local state based on response
          return response;
        } catch (error) {
          log.error("Failed to post integration", error);
          throw error;
        }
      } else {
        log.warn("EmailOctopus integration not found or ID is missing.");
        return null; // Or handle as appropriate
      }
    }),

    resetData() {
      self.integrations.clear();
      self.availableIntegrations.clear();
    },
  }));

export type IIntegrationStore = Instance<typeof IntegrationStore>; 